import classNames from 'classnames';
import { useState, useEffect } from 'react';
import useCollectionSelectItemTracking from 'hooks/tracking/useCollectionSelectItemTracking';
import { useSettings } from 'hooks';
import { reformatLocaleToContentfulLocale } from 'lib/utils/helpers';
import useSWR from 'swr';
import { useGlobalModules } from 'contexts';
import ProductCard from 'components/shared/productCard';
import Slider from 'components/shared/slider';
import getShopifyData from 'lib/shopify/getShopifyData';
import styles from './relatedProducts.module.scss';

const RelatedProducts = ({ className, locale, data, productId }) => {
  const { setSaveItemSelectionList } = useSettings();
  const [items, setItems] = useState([]);
  const globalModules = useGlobalModules();
  const { translations } = globalModules;
  const { relatedProductsDefaultTitle } = translations.product;
  const { title, relatedProducts, anchorId = 'sectionRelatedProducts' } = data || {};

  const fetcher = async endpoint => {
    const responseRaw = await fetch(endpoint);
    const response = await responseRaw.json();
    return response;
  };

  const swrOptions = {
    refreshInterval: 60000,
  };

  const { data: productRecommendations } = useSWR(
    productId && !relatedProducts?.length
      ? `/api/shopify/utils/getProductRecommendations?productId=${productId}&locale=${locale}`
      : null,
    fetcher,
    swrOptions
  );

  useEffect(() => {
    if (!productRecommendations?.data?.length) return;

    const localisedProducts = productRecommendations?.data?.filter(product =>
      product.availableLocales?.find(
        productLocale => reformatLocaleToContentfulLocale(locale) === productLocale
      )
    );

    if (!localisedProducts) return;

    // setItems(localisedProducts);
    setItems(localisedProducts);
  }, [productRecommendations, locale]);

  useEffect(() => {
    if (!relatedProducts?.length) return;
    const localisedProducts = relatedProducts?.filter(product =>
      product.availableLocales?.find(
        productLocale => reformatLocaleToContentfulLocale(locale) === productLocale
      )
    );
    if (!localisedProducts) return;

    const updateProductData = (product, shopifyData) => {
      const updatedShopifyData = shopifyData?.find(
        ({ productSKU }) => productSKU === product?.shopifyData?.variants[0]?.sku
      );
      if (product?.shopifyData) {
        // eslint-disable-next-line no-param-reassign
        product.shopifyData.variants[0] = {
          ...product.shopifyData.variants[0],
          ...(updatedShopifyData?.productCompareAtPrice && {
            compareAtPrice: updatedShopifyData?.productCompareAtPrice,
          }),
          outOfStock: updatedShopifyData?.outOfStock,
          ...(updatedShopifyData?.productPrice && { price: updatedShopifyData?.productPrice }),
          ...(updatedShopifyData?.productTitle && { title: updatedShopifyData?.productTitle }),
        };
      }
    };

    const filterOutOfStock = arr => {
      const filteredProducts = arr.filter(item => !item.shopifyData?.variants[0]?.outOfStock);

      return filteredProducts;
    };

    const shopifyDataRetrieval = async () => {
      if (localisedProducts.length > 0) {
        const productIds = localisedProducts.map(
          product => `gid://shopify/Product/${product.shopifyData?.variants[0].product_id}`
        );

        const pdpShopifyData = await getShopifyData({ productIds, locale });

        if (pdpShopifyData.length > 0) {
          localisedProducts.forEach(product => {
            updateProductData(product, pdpShopifyData);
            const cardData = pdpShopifyData.find(
              ({ productSKU }) => product.shopifyData?.variants[0]?.sku === productSKU
            );
            // eslint-disable-next-line no-param-reassign
            product.cardShopifyData = cardData;
          });
        }

        setItems(filterOutOfStock(localisedProducts));
      }
    };

    shopifyDataRetrieval();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relatedProducts]);

  const trackSelectItem = (localizedProduct, position) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useCollectionSelectItemTracking({
      product: {
        name: localizedProduct.cardShopifyData?.productTitle,
        id: localizedProduct.shopifyData?.variants?.[0]?.product_id,
        category: localizedProduct?.title?.split(' ')?.[0],
        category2: localizedProduct?.title?.slice(-1)?.[0],
        price: localizedProduct.cardShopifyData?.productPrice,
        position,
      },
      list: 'Related products',
      setSaveItemSelectionList,
      locale,
    });
  };

  if (!items.length) return null;

  return (
    <section id={anchorId} className={classNames(className, styles.relatedProducts)}>
      {items.length > 0 && (
        <>
          <Slider
            title={title || relatedProductsDefaultTitle}
            className={styles.relatedProductsSlider}
          >
            {items?.map((localizedProduct, position) => (
              <ProductCard
                onClick={() => {
                  trackSelectItem(localizedProduct, position);
                }}
                locale={locale}
                className={styles.relatedProductsItem}
                itemData={localizedProduct}
                key={localizedProduct.slug}
                itemIndex={position}
                pageTitle="Related Products"
              />
            ))}
          </Slider>
        </>
      )}
    </section>
  );
};

export default RelatedProducts;
